import { BloodType, useGetWoocommerceProductsLazyQuery } from 'gql/types';
import { ControlledSelect } from 'modules/common/components';
import { PRODUCT_ID } from 'modules/products/constants/productId';
import { FC, useEffect } from 'react';
import { UseControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { productByBloodType } from './productsByBloodType';
import styles from './styles.module.scss';

interface IProductTypeOptions {
  value: number;
  label: string;
}

type PropTypes = {
  productCategoryId?: number;
  controlled: UseControllerProps<any>;
  donorBloodType?: BloodType;
  onChangeDefaultValue?: (value: { label: string; value: number }) => void;
};

const ProductTypeSelect: FC<PropTypes> = ({
  productCategoryId,
  controlled,
  donorBloodType,
  onChangeDefaultValue,
}) => {
  const { t } = useTranslation('products.ProductTypeSelect');

  const selectAllowedProducts = (allProducts: IProductTypeOptions[]) => {
    return allProducts.filter(({ value }) => {
      const isAllowed = Object.values(PRODUCT_ID).includes(value);
      const isAllowedForBloodType = donorBloodType
        ? productByBloodType[donorBloodType].includes(value)
        : true;
      return isAllowed && isAllowedForBloodType;
    });
  };

  const [getWoocommerceProduct, getWoocommerceProductQuery] =
    useGetWoocommerceProductsLazyQuery({
      variables: {
        input: {
          category: productCategoryId,
        },
      },
      onCompleted: ({ getWoocommerceProducts }) => {
        const products = getWoocommerceProducts.data.map(({ id, name }) => ({
          value: id,
          label: name,
        }));

        const allowedProducts = selectAllowedProducts(products ?? []);

        const defaultValue =
          allowedProducts.length === 1 ? allowedProducts[0] : undefined;

        defaultValue && onChangeDefaultValue?.(defaultValue);
      },
    });

  useEffect(() => {
    if (productCategoryId !== undefined) {
      getWoocommerceProduct({
        variables: {
          input: {
            category: productCategoryId,
          },
        },
      });
    }
  }, [productCategoryId]);

  const products =
    getWoocommerceProductQuery.data?.getWoocommerceProducts.data.map(
      ({ id, name }) => ({ value: id, label: name })
    );

  const allowedProducts = selectAllowedProducts(products ?? []);

  return (
    <ControlledSelect
      loading={getWoocommerceProductQuery.loading}
      className={styles.select}
      options={allowedProducts}
      controlled={controlled}
      placeholder={t('selectProductType')}
    />
  );
};

export default ProductTypeSelect;
